import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  windows: {}, // { [windowId]: { id, url, domain, uuid, token, encryptionPassword, path, injectOnLoad, loading } }
  activeWindowId: null, // ID активного окна
  windowOrder: [], // порядок открытия для UI табов
};

let nextWindowId = 1;

const externalAppWindowsSlice = createSlice({
  name: 'externalAppWindows',
  initialState,
  reducers: {
    openExternalAppWindow: (state, action) => {
      const { domain, startInBackground = false, replace = false } = action.payload;

      // Check if window with this domain already exists
      const existingWindowId = Object.keys(state.windows).find(
        (id) => state.windows[id].domain === domain
      );

      // `replace` drops the existing window so a fresh one is created below (used when a launch
      // injects a new payload); otherwise an existing window is just re-activated.
      if (existingWindowId && replace) {
        delete state.windows[existingWindowId];
        state.windowOrder = state.windowOrder.filter((id) => id !== existingWindowId);
        if (state.activeWindowId === existingWindowId) state.activeWindowId = null;
      }

      if (existingWindowId && !replace) {
        // Activate existing window (unless starting in background)
        if (!startInBackground) {
          state.activeWindowId = existingWindowId;
        }
      } else {
        // Create new window
        const windowId = `window-${nextWindowId++}`;
        state.windows[windowId] = {
          id: windowId,
          url: action.payload.url,
          domain: action.payload.domain,
          uuid: action.payload.uuid,
          token: action.payload.token,
          encryptionPassword: action.payload.encryptionPassword,
          path: action.payload.path,
          injectOnLoad: action.payload.injectOnLoad,
          loading: true,
          notifications: {},
        };
        state.windowOrder.push(windowId);
        // Only activate if not starting in background
        if (!startInBackground) {
          state.activeWindowId = windowId;
        }
      }
    },
    closeExternalAppWindow: (state, action) => {
      const windowId = action.payload;
      delete state.windows[windowId];
      state.windowOrder = state.windowOrder.filter((id) => id !== windowId);

      // Если закрываем активное окно, переключаемся на последнее в списке
      if (state.activeWindowId === windowId) {
        state.activeWindowId =
          state.windowOrder.length > 0 ? state.windowOrder[state.windowOrder.length - 1] : null;
      }
    },
    setActiveWindow: (state, action) => {
      const windowId = action.payload;
      if (windowId === null || state.windows[windowId]) {
        state.activeWindowId = windowId;
      }
    },
    setWindowLoading: (state, action) => {
      const { windowId, loading } = action.payload;
      if (state.windows[windowId]) {
        state.windows[windowId].loading = loading;
      }
    },
    updateNotifications: (state, action) => {
      const { windowId, notifications } = action.payload;
      if (state.windows[windowId]) {
        // Only update if notifications actually changed
        const currentNotifications = state.windows[windowId].notifications;
        const hasChanged = JSON.stringify(currentNotifications) !== JSON.stringify(notifications);

        if (hasChanged) {
          state.windows[windowId].notifications = notifications;
        }
      }
    },
    closeAllExternalAppWindows: (state) => {
      state.windows = {};
      state.activeWindowId = null;
      state.windowOrder = [];
    },
  },
});

export const {
  openExternalAppWindow,
  closeExternalAppWindow,
  setActiveWindow,
  setWindowLoading,
  updateNotifications,
  closeAllExternalAppWindows,
} = externalAppWindowsSlice.actions;

// Selectors
export const selectAllWindows = (state) => state.externalAppWindows.windows;
export const selectActiveWindowId = (state) => state.externalAppWindows.activeWindowId;
export const selectWindowOrder = (state) => state.externalAppWindows.windowOrder;
export const selectActiveWindow = (state) => {
  const activeId = state.externalAppWindows.activeWindowId;
  return activeId ? state.externalAppWindows.windows[activeId] : null;
};

// Unread count for the window (if any) currently hosting the given app domain.
export const getAppUnreadCount = (windows, domain) => {
  const appWindow = Object.values(windows).find((w) => w.domain === domain);
  return appWindow?.notifications?.unreadCount || 0;
};

export default externalAppWindowsSlice.reducer;
